import Img from "./img";
import Logo from "./logo";
import Link from "next/link";
import { useRouterContext } from "../contexts";
import {
  LS_Get,
} from "../utils/local-storage";

import AccountButtons from "./account-buttons";
import MobileTopNav from "./mobile-top-nav";
import NotifyBell from "./notify-bell";
import GuideSlideshowButton from "./guides/GuideSlideshowButton";
import Ad from "./ad";
import { useState, useEffect } from "react";
import { useNotifications, useProduct } from "../contexts";
import shared from "../data/js/shared";
import { AdFramework } from "./ad-framework";
import { DESKTOP_AD_MIN_WIDTH } from "../utils/ad-layout";
import { IS_DEV, IS_MOBILE } from "../data/js/server-constants";
import { hardNavigate } from '../utils/hard-navigate';
const { sellProductLabel, marketplaceLabel } = shared.product;


const TopHeader = ({
  children,
  showAd,
  setMobileTopNav,
  setLoginOpen,
  setProductOpen,
  post,
  headerTitle,
  titleClick,
  account,
  setNotificationDrawerOpen,
  includeNotifications,
  transparentBG
}) => {
  const { unreadMessagesCount, unreadSalesCount, unreadPurchasesCount, unreadTransactionsCount, systemNoticesCount } = useNotifications();
  const { removeProduct, productDetail } = useProduct();

  // Create state object for backward compatibility
  // Use a consistent default for SSR
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- post-hydration mount flag; gates client-only rendering (window.history, desktop button) to avoid SSR hydration mismatch and cannot be derived during render
    setMounted(true);
  }, []);

  const state = {
    history: mounted && typeof window !== 'undefined' ? window.history : { length: 1 },
    productDetail
  };

  const totalNoticeNum =
    unreadMessagesCount +
    unreadSalesCount +
    unreadPurchasesCount +
    unreadTransactionsCount +
    systemNoticesCount;

  const [promoTipOpen, setPromoTipOpen] = useState(false);
  const { pathname } = useRouterContext();

  useEffect(() => {
    setTimeout(() => {
      const closedPromo = LS_Get("closedPromo") || false;
      setPromoTipOpen(!closedPromo);
    }, 1000);
  }, []);

  // Determine post status based on pathname for consistent SSR
  const isPost = pathname.includes("sell") || pathname.startsWith("/account") || false;

  // Keys are route patterns: the router context's pathname is Next's
  // `router.pathname`, so a detail page reads '/account/sales/[id]'.
  const ACCOUNT_GUIDE_MAP: Record<string, { slug: string; category: 'seller' | 'buyer' }> = {
    '/account': { slug: 'my-profile', category: 'seller' },
    '/account/profile': { slug: 'my-profile', category: 'seller' },
    '/account/addresses': { slug: 'my-addresses', category: 'seller' },
    '/account/shipping': { slug: 'shipping-settings', category: 'seller' },
    '/account/wallet': { slug: 'my-wallet', category: 'seller' },
    '/account/my-listings': { slug: 'my-listings', category: 'seller' },
    '/account/my-listings/[id]': { slug: 'editing-a-listing', category: 'seller' },
    '/account/sales': { slug: 'my-sales', category: 'seller' },
    '/account/sales/[id]': { slug: 'shipping-and-fulfillment', category: 'seller' },
    '/account/discounts': { slug: 'my-discounts', category: 'seller' },
    '/account/notifications': { slug: 'notification-preferences', category: 'seller' },
    '/account/purchases': { slug: 'my-purchases', category: 'buyer' },
    '/account/purchases/[id]': { slug: 'tracking-your-order', category: 'buyer' },
    '/account/wishlist': { slug: 'my-wishlist', category: 'buyer' },
    '/account/chats': { slug: 'my-chats', category: 'buyer' },
    '/account/chats/[id]': { slug: 'my-chats', category: 'buyer' },
  };
  const accountGuide = ACCOUNT_GUIDE_MAP[pathname];
  const url = isPost ? "/" : "/sell/";
  const topButtonLabel = isPost ? marketplaceLabel : sellProductLabel;

  const isGuidesPage = pathname.startsWith('/guides');
  // The dash takes the account area's header: fixed at 70px, which is what
  // `.STATE--dash-area main`'s top padding is measured against. The default
  // `smaller-header` is position:absolute, so it scrolled away with the page.
  const isDashPage = pathname.startsWith('/dash');

  /**
   * Checkout is a payment form, and the header's three ways off it are three
   * ways to abandon a purchase mid-flow: Sell walks the buyer to the other side
   * of the marketplace, the guide button opens a slideshow over the form, and
   * the bell opens a drawer. None of them helps anyone finish paying, so the
   * header keeps the logo and a plain "Checkout" title. No "Account /" crumb
   * (owner, 2026-10-25): its Account link is one more way off the form. The
   * full Sellwild logo shows on a phone too, not the S badge (owner, 2026-11-03).
   */
  const isCheckout = pathname.startsWith('/checkout');
  const headerType = `${(account || isDashPage) && !state.productDetail ? "account" : (post ? "post" : !showAd ? "smaller" : "top")}-header ${transparentBG ? 'transparentBG' : ''} same-color ${state.productDetail ? "product-open" : ""} ${isGuidesPage ? '!bg-transparent backdrop-blur-xl' : ''} ${isCheckout ? 'checkout-header' : ''}`

  const op = titleClick ? {
    onClick: (e) => titleClick(e)
  } : {};

  return (
    <>
      <div className={`top-mobile-cover same-color ${isGuidesPage ? '!bg-transparent' : ''}`} />
      {!(account || !showAd) && <TopAd />}
      <div
        className={`${headerType} ${
          promoTipOpen ? "promo-open" : ""
        } ${state.productDetail ? "siteimpact" : ""}`}
        data-testid="header-container"
      >
        {/* Desktop button - only render after mount to avoid hydration issues */}
        {mounted && !isCheckout && (
          <Link href={url} className="top-post-button" onClick={removeProduct} data-testid="header-sell-btn">
            <span>{topButtonLabel}</span>
          </Link>
        )}

        {/* Show MobileTopNav toggle on mobile - always show on main pages.
            Not on the dash: Shop/Sell is a storefront control, and on mobile it
            landed on top of the DASH title. */}
        {((!account && !isDashPage) || state.productDetail) && (
          <MobileTopNav
            state={state as unknown as string}
            setMobileTopNav={setMobileTopNav}
            setLoginOpen={setLoginOpen}
            setProductOpen={setProductOpen}
            removeProduct={removeProduct}
          />
        )}
        <Link
          href="/"
          aria-label="Sellwild Logo"
          data-testid="header-logo"
          onClick={(ev) => {
            ev.preventDefault();

            // Clear filter preferences from localStorage
            localStorage.removeItem('filter-panel-preferences');

            // Force a full page reload to home page to clear all state
            hardNavigate('/');
          }}
        >
          <Logo className="" />
          <div className="mobileLogo">
            <Img className="svg letterS" src="/images/svg/S.svg" />
            <Img className="svg bgGreen" src="/images/svg/Union.svg" />
          </div>
        </Link>
        {isDashPage && (
          // On mobile it sits to the right of the rail hamburger (left:52px,
          // 38px wide) and fits inside the 70px header; the desktop placement
          // is unchanged.
          <Link href="/dash" aria-label="Dashboard" className="absolute left-[100px] top-[16px] md:top-[0px] md:left-[235px]">
            <img src="/images/page-titles/title-dash.png" alt="Dashboard" className="h-10 md:h-[82px]" />
          </Link>
        )}
        {(headerTitle && !state.productDetail ? (
          <h1 {...op} className="page-title highlight" data-testid="header-title">
            {account && !isCheckout && pathname !== "/account" && pathname !== "/account/" ? (
              <>
                <Link href="/account/" className="breadcrumb-link">
                  Account
                </Link>
                <span className="breadcrumb-separator"> / </span>
                {/* Check if we're on a detail page (3 levels deep) */}
                {/* pathname is the route pattern ('/account/sales/[id]'), which
                    a [\w-]+ segment never matched, so the middle crumb never showed */}
                {/^\/account\/(sales|purchases)\/[^/]+/.test(pathname) ? (
                  <>
                    <Link
                      href={pathname.includes('/sales/') ? '/account/sales' : '/account/purchases'}
                      className="breadcrumb-link"
                    >
                      {pathname.includes('/sales/') ? 'My Sales' : 'My Purchases'}
                    </Link>
                    <span className="breadcrumb-separator"> / </span>
                    <span className="breadcrumb-current">{headerTitle}</span>
                  </>
                ) : (
                  <span className="breadcrumb-current">{headerTitle}</span>
                )}
              </>
            ) : (
              headerTitle
            )}
          </h1>
        ) : "")}
        {children}
        {account && !state.productDetail && !isCheckout && <div className="account-buttons">
          {!pathname.startsWith('/guides') && <GuideSlideshowButton guideCategory={accountGuide?.category ?? (isPost ? "seller" : "buyer")} guideSlug={accountGuide?.slug} />}
          {includeNotifications &&
            <div
              className={`${
                totalNoticeNum > 0 ? "STATE--unread" : ""
              } notify-button`}
              data-testid="header-notify-btn"
              onClick={() => {
                setNotificationDrawerOpen(true);
              }}
            >
              {totalNoticeNum > 0 && <div className="unreadCount" data-testid="header-notify-count">{totalNoticeNum}</div>}
              <NotifyBell />
            </div>
          }
        </div>}
        {(!account) &&
          <div className="account-buttons">
            {!pathname.startsWith('/guides') && <GuideSlideshowButton guideCategory={accountGuide?.category ?? (isPost ? "seller" : "buyer")} guideSlug={accountGuide?.slug} />}
            <AccountButtons />

        </div>}
        <div
          className="
          top-banner-ad
          google-ad
          product-top-ad
        "
        />
      </div>
    </>
  );
};

/**
 * Below this the top banner is not rendered at all. Matches the breakpoint in
 * styles/components/top-header.scss where `.list-top-ad` stops being the native
 * status-bar strip and becomes a real in-flow 728x90 slot.
 */
const TOP_AD_MIN_WIDTH = DESKTOP_AD_MIN_WIDTH;

const TopAd = () => {
  /**
   * Desktop widths only.
   *
   * Under 728px `.list-top-ad` is `position: fixed; top: 0; height:
   * env(safe-area-inset-top)` -- the NATIVE app's status-bar strip, where this
   * component never renders (`IS_MOBILE` below is the Capacitor build flag, not
   * the browser). Mobile web sets `viewport-fit=cover` (components/head.tsx), so
   * on a notched iPhone that inset is a real ~47px: the 320x50 was being pinned
   * over the scrolling page, clipped, underneath the opaque `.top-mobile-cover`
   * at a higher z-index. Nobody could ever see it, and it flickered into view
   * every time Safari resized the inset during a scroll -- which is why it never
   * reproduced in a desktop browser's device mode, where the inset is 0 and the
   * strip collapses to 9px.
   *
   * Gated in React rather than with `display: none`, because a mounted
   * <sellwild-ad> registers a GAM slot and requests the impression whether or
   * not its box is ever painted, and an impression nobody can see is one we
   * should not ask for. Measured once on mount and not tracked across resizes:
   * remounting the tag would define a SECOND GAM slot for the same space and
   * start another auction.
   */
  const [wideEnough, setWideEnough] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- viewport width does not exist during SSR or the hydration render, so it cannot be derived while rendering
    setWideEnough(window.innerWidth >= TOP_AD_MIN_WIDTH);
  }, []);

  if (IS_DEV) return null;

  return (
    <div className="list-top-ad">
    {!IS_MOBILE && wideEnough && <AdFramework
      bidstream={{
        mobile: (
          <div
            data-ad-tagid="MgQGXBYSCw7tyxIwr83m"
            data-zid="1020"
            data-name="SW - sellwild.com mobile 320x50"
          />
        ),
        desktop: (
          <div
            data-ad-tagid="Yp4BKeY0meZZk0eOYBue"
            data-zid="1014"
            data-name="SW - sellwild.com desktop 728x90"
          />
        ),
      }}
      adsense={
        <Ad
          key={"4883427378"}
          className="adsbygoogle"
          style={{ display: "block", margin: "auto", maxWidth: 728, minHeight: 90 }}
          data-ad-client="ca-pub-3154552656014103"
          data-ad-slot="4883427378"
        />
      }
    />}
  </div>
  )
}

export default TopHeader;

// {state.api !== "prod" ? (
//   <div className="stagingAPI">STAGING API</div>
// ) : (
//   ""
// )}
